// Castintech dashboard — Ekip (Agents) view modulu.
// Faz 9b PR8 — ekip view + accounts/fallback kartlari (~190 satir, 9 export):
// - Helpers: agentTasks, agentActiveTasks (drawer + topbar strip da kullaniyor)
// - Render: renderEkip (4 stat card + agent grid)
// - Accounts: accountAction, accountSwitch, accountRemove, renderAccountsCard, fetchAccounts
// - Fallback: renderFallbackCard
//
// State erisimi: state.js direct import.
// Utils: utils.js (escAttr/getGem/getModelBadge/statusCls/fmtTtl).
// Api: api.js (postAccountAction/getAccounts).
// toast, refresh, openAgentDrawer henuz HTML'de — window.X?.() ile cagrilir.

import { state } from '../state.js';
import * as Api from '../api.js';
import { escAttr, getGem, getModelBadge, statusCls, fmtTtl, $, asEl } from '../utils.js';
import { toast } from '../ui/toast.js';
import { openAgentDrawer } from '../ui/drawer.js';
import type { Issue } from '../../types/api';

// ── Türkçe görünen-ad + karakter yüzü katmanı (display-only, /characters/) ──
interface DisplayName { person: string; code: string; role: string; gem: string; }
let _dn: Record<string, DisplayName> = {};
let _dnState: 'none' | 'loading' | 'done' = 'none';
function loadDisplayNames(after: () => void): void {
  if (_dnState !== 'none') return;
  _dnState = 'loading';
  fetch('/characters/display-names.json', { cache: 'no-store' }).then(r => r.json()).then(j => { _dn = j || {}; _dnState = 'done'; after(); }).catch(() => { _dnState = 'done'; });
}
function agentSlug(name: string): string { return (name || '').toLowerCase().trim().replace(/\s+/g, '-'); }

// ── Helpers (drawer + topbar strip da kullanir) ─────────────────────

export function agentTasks(aid: string): Issue[] {
  return state.tasks.filter(t => t.assigneeAgentId === aid || t.assignee_id === aid);
}

export function agentActiveTasks(aid: string): Issue[] {
  return agentTasks(aid).filter(t => ['open', 'in_progress'].includes((t.status || '').toLowerCase()));
}

// ── Ekip view render ────────────────────────────────────────────────

export function renderEkip() {
  loadDisplayNames(renderEkip); // ilk render'da Türkçe ad + karakter yüzünü getir, sonra yeniden çiz
  // Faz 47.2 S3.A — Audit "Aktif: 0 hep, error agent neden error görünmüyor"
  // bulgusunu fix:
  //  - "Aktif" sayısı paperclip status='active'|'running' yerine "in_progress
  //    issue'su olan agent"i ifade eden gerçek-anlamlı metriğe çevrildi (Çalışan).
  //  - Error agent count görünür hale geldi (Hatalı stat card).
  //  - Idle/Bekliyor sayısı tutarlılık için; toplamlar agents.length'e eşit.
  // Çalışan = busy CSS class (paperclip 'working' → busy mapped Faz 47.2).
  const nowMs = Date.now();
  const agentsBusy = new Set<string>();
  let running = 0;
  let done = 0;
  for (const t of state.tasks) {
    const s = (t.status || '').toLowerCase();
    if (s === 'open' || s === 'in_progress') {
      running++;
      const aid = t.assigneeAgentId || t.assignee_id;
      if (aid) agentsBusy.add(String(aid));
    }
    else if ((s === 'closed' || s === 'done' || s === 'completed')
        && (nowMs - new Date(t.updatedAt || 0).getTime()) < 86400000) done++;
  }
  // "Çalışan" — runtime 'busy' ya da assigned in_progress task'i olan agent.
  const calisan = state.agents.filter(a => {
    const sc = statusCls(a.status);
    return sc === 'busy' || sc === 'active' || (a.id && agentsBusy.has(String(a.id)));
  }).length;
  const hatali = state.agents.filter(a => statusCls(a.status) === 'error').length;

  const statsRow = $('stats-row'); if (statsRow) statsRow.innerHTML = [
    { label: 'Toplam Ajan', val: state.agents.length, color: '#2dd4bf' },
    { label: 'Çalışan', val: calisan, color: '#4ade80' },
    { label: 'Hatalı', val: hatali, color: hatali > 0 ? '#ef4444' : '#71717a' },
    { label: 'Devam Eden', val: running, color: '#60a5fa' },
    { label: 'Bugün Tamam', val: done, color: '#fbbf24' },
  ].map((s, i) => `
    <div class="stat-card" style="animation-delay:${i * 60}ms;animation:card-enter .3s ease-out both">
      <div class="stat-label">${s.label}</div>
      <div class="stat-value" style="color:${s.color}">${s.val}</div>
    </div>`).join('');

  const displayed = state.agentFilter ? state.agents.filter(a => a.id === state.agentFilter) : state.agents;
  if (!displayed.length) {
    const ag = $('agent-grid'); if (ag) ag.innerHTML = `<div class="empty-state" style="grid-column:1/-1"><div class="empty-icon">💎</div><div class="empty-title">Ajan bulunamadı</div></div>`;
    return;
  }
  const ag = $('agent-grid'); if (ag) ag.innerHTML = displayed.map((a, i) => {
    const gem = getGem(a.name);
    // slug: önce a.name (forge, castacc-gm), eşleşmezse gem canonical adı (Garnet→garnet).
    const slug0 = agentSlug(a.name);
    const slug = _dn[slug0] ? slug0 : agentSlug(gem.name);
    const dn = _dn[slug];
    const sc = statusCls(a.status);
    const at = a.id ? agentActiveTasks(a.id) : [];
    const cur = at[0];
    const pulseClass = sc === 'active' ? 'pulse-active' : sc === 'busy' ? 'pulse-busy' : '';
    // Faz 47.2 S3.A — Error agent için sebep ipucu (last_error veya son
    // başarısız task title). Tıkla → drawer'da run history.
    const lastErrRaw = (a as { last_error?: unknown }).last_error;
    const lastErr = typeof lastErrRaw === 'string' ? lastErrRaw : '';
    const errorHint: string = sc === 'error'
      ? (lastErr || (cur ? `son: ${(cur.title || '').substring(0, 40)}` : 'sebep: tıkla'))
      : '';
    return `
      <div class="agent-card ${gem.cls} ${pulseClass}" style="animation-delay:${i * 60}ms" data-action="open-agent" data-agent-name="${escAttr(a.name)}"${dn ? ` data-anim="${escAttr(slug)}"` : ''}>
        ${at.length ? `<div class="ac-task-count">${at.length}</div>` : ''}
        <div class="ac-avatar ${gem.cls}">
          <div class="ac-avatar-bg"></div>
          ${dn ? `<img class="ac-face" src="/characters/face/${slug}.png?v=3" loading="lazy" onerror="this.style.display='none';this.parentElement.querySelector('.ac-hero-letter').style.display='flex'">` : ''}
          <div class="ac-hero-letter"${dn ? ' style="display:none"' : ''}>${gem.name[0]}</div>
          ${gem.icon ? `<div class="ac-hero-badge">${gem.icon}</div>` : ''}
          <div class="ac-avatar-shine"></div>
        </div>
        <div class="ac-name">${dn ? dn.person : gem.name}</div>
        <div class="ac-role">${dn ? `${dn.code} · ${dn.role}` : (gem.role || a.title || '')}${a.company_name ? ` · <span style="color:var(--muted);font-size:10px">${a.company_name}</span>` : ''}</div>
        ${getModelBadge(a)}
        <div class="ac-status">
          <div class="ac-pill pill-${sc}">
            <span class="dot"></span>${{ active: 'Aktif', busy: 'Meşgul', error: 'Hata', idle: 'Bekliyor' }[sc]}
          </div>
          ${cur ? `<div class="ac-task">${(cur.title || '').substring(0, 18)}</div>` : ''}
        </div>
        ${errorHint ? `<div class="ac-error-hint" title="${escAttr(errorHint)}">⚠️ ${errorHint.substring(0, 40)}</div>` : ''}
      </div>`;
  }).join('');
  bindEkipHover();
}

// ── D4 — per-ajan hover animasyonu (Remotion mp4). Delegated, tek video, reduced-motion saygılı. ──
let _ekipHoverBound = false;
function bindEkipHover(): void {
  if (_ekipHoverBound) return;
  const grid = $('agent-grid');
  if (!grid) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { _ekipHoverBound = true; return; }
  _ekipHoverBound = true;
  let activeCard: HTMLElement | null = null;
  const stop = () => {
    if (!activeCard) return;
    activeCard.querySelector('.ac-anim')?.remove();
    activeCard = null;
  };
  grid.addEventListener('mouseover', (e: MouseEvent) => {
    const card = (e.target as HTMLElement)?.closest<HTMLElement>('.agent-card[data-anim]');
    if (!card || card === activeCard) return;
    stop();
    const slug = card.getAttribute('data-anim'); if (!slug) return;
    const av = card.querySelector('.ac-avatar'); if (!av) return;
    const v = document.createElement('video');
    v.className = 'ac-anim'; v.muted = true; v.loop = true; v.playsInline = true; v.autoplay = true;
    v.src = `/characters/anim/${slug}.mp4`;
    v.onerror = () => v.remove();
    av.appendChild(v);
    const p = v.play(); if (p && p.catch) p.catch(() => {});
    activeCard = card;
  });
  grid.addEventListener('mouseout', (e: MouseEvent) => {
    if (!activeCard) return;
    const to = e.relatedTarget as Node | null;
    if (to && activeCard.contains(to)) return;
    stop();
  });
}

// ── Claude Accounts (accountAction/Switch/Remove + render + fetch) ──

interface AccountActionResult {
  ok?: boolean;
  error?: string;
  [key: string]: unknown;
}

export async function accountAction(path: string, body: { name?: string }): Promise<AccountActionResult> {
  try {
    const r = await Api.postAccountAction(path, body);
    const d: AccountActionResult = await r.json();
    return d;
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export async function accountSwitch(name: string): Promise<void> {
  // accountRemove ile aynı desen: sistem-geneli mutasyon onaysız yapılmaz.
  if (!window.confirm('"' + name + '" aktif Claude hesabı yapılacak.\n\nBu değişiklik SİSTEM GENELİNDE tüm ajan çağrılarını etkiler. Devam?')) return;
  const d = await accountAction('switch', { name });
  if (d.ok) { toast('Aktif hesap: ' + name, 'success'); fetchAccounts(); }
  else toast('Hata: ' + (d.error || 'switch failed'), 'error');
}

export async function accountRemove(name: string): Promise<void> {
  if (!window.confirm('"' + name + '" hesabını arşive taşı?\n\n(Dosya .archive/ altında saklanır, geri alınabilir.)')) return;
  const d = await accountAction('remove', { name });
  if (d.ok) { toast('Arşivlendi: ' + name, 'success'); fetchAccounts(); }
  else toast('Hata: ' + (d.error || 'remove failed'), 'error');
}

export function renderAccountsCard() {
  const el = document.getElementById('accounts-card');
  if (!el) return;
  if (!state.accounts) {
    el.innerHTML = '<div class="ac-head"><div><div class="ac-title">Claude Hesapları</div><div class="ac-sub">Yükleniyor...</div></div></div>';
    return;
  }
  const list = state.accounts.list || [];
  const encOn = state.accounts.encryptionEnabled;
  const activeName = (list.find(a => a.active) || {}).name || '—';
  el.innerHTML = `
    <div class="ac-head">
      <div>
        <div class="ac-title">Claude Hesapları <span style="font-size:11px;color:var(--muted);font-weight:400">· aktif: ${activeName}</span></div>
        <div class="ac-sub">${list.length} hesap · şifreleme: ${encOn ? 'açık 🔒' : 'kapalı'} · ekle/değiştir için sağ alttaki 🤖</div>
      </div>
    </div>
    ${list.length === 0
      ? (state.accounts.loadError
          ? '<div class="acct-empty" style="color:var(--danger,#e55);">Hesap listesi alınamadı (yetki veya bağlantı hatası) — Yenile ile tekrar deneyin.</div>'
          : '<div class="acct-empty">Hiç hesap yok. Sağ alttaki 🤖 butonundan ekleyin.</div>')
      : '<div class="accounts-list">' + list.map(a => {
          const ttl = fmtTtl(a.expiresInSec);
          const status = a.status || 'unknown';
          const statusLbl = { ok: 'ok', soon: 'yakında dolar', expired: 'süresi dolmuş', unknown: '?' }[status] || status;
          const meta = [
            a.subscriptionType || null,
            ttl ? 'kalan ' + ttl : null,
            a.encrypted ? 'şifreli 🔒' : 'düz metin ⚠️',
          ].filter(Boolean).join(' · ');
          const actions = a.active
            ? '<span class="acct-active-pill">AKTİF</span>'
            : `<button class="btn-ghost" data-action="account-switch" data-account-name="${escAttr(a.name)}" style="font-size:11px;padding:4px 10px">Aktif et</button>
               <button class="btn-ghost" data-action="account-remove" data-account-name="${escAttr(a.name)}" style="font-size:11px;padding:4px 8px;color:#f87171;border-color:#ef444444" title="Arşive taşı">Sil</button>`;
          return `<div class="acct-row ${a.active ? 'active' : ''}">
            <div class="acct-left">
              <div class="acct-name">${a.name}<span class="acct-badge ${status}">${statusLbl}</span></div>
              <div class="acct-meta">${meta}</div>
            </div>
            <div class="acct-right">${actions}</div>
          </div>`;
        }).join('') + '</div>'
    }`;
}

export async function fetchAccounts() {
  try {
    const r = await Api.getAccounts();
    if (!r.ok) {
      // Hata ≠ boş: yetki/sunucu hatasında "hesap yok" gösterme.
      state.accounts = { list: [], encryptionEnabled: false, loadError: true };
      renderAccountsCard();
      return;
    }
    const d = await r.json();
    state.accounts = { list: d.accounts || [], encryptionEnabled: !!d.encryptionEnabled };
    renderAccountsCard();
  } catch {
    state.accounts = { list: [], encryptionEnabled: false, loadError: true };
    renderAccountsCard();
  }
}

// ── Fallback router 24h kart ────────────────────────────────────────

const FALLBACK_COLORS: Record<string, string> = {
  claude: '#4ade80', codex: '#fbbf24', gemini: '#60a5fa', openrouter: '#f87171', router: '#94a3b8',
};

export function renderFallbackCard() {
  const el = document.getElementById('fallback-card');
  if (!el) return;
  const s = state.fallbackStats;
  if (!s || !s.total) { el.style.display = 'none'; return; }
  el.style.display = 'block';
  const tiers = Object.entries(s.byTier).sort((a, b) => (b[1].ok + b[1].fail) - (a[1].ok + a[1].fail));
  el.innerHTML = `
    <div class="fc-head">
      <div>
        <div class="fc-title">Fallback Durumu — son 24 saat</div>
        <div class="fc-sub">${s.total} çalışma · Claude→Codex→Gemini→OpenRouter zinciri</div>
      </div>
    </div>
    <div class="fc-tiers">
      ${tiers.map(([name, v]) => {
        const color = FALLBACK_COLORS[name] || '#94a3b8';
        const total = v.ok + v.fail;
        const pct = s.total ? Math.round(total * 100 / s.total) : 0;
        return `<div class="fc-tier" style="border-color:${color}40">
          <div class="fc-tier-name" style="color:${color}">${name}</div>
          <div class="fc-tier-val">${total} <span style="font-size:11px;font-weight:400;color:var(--muted)">(%${pct})</span></div>
          <div style="font-size:10px;color:var(--muted);margin-top:2px">ok ${v.ok} · fail ${v.fail}</div>
        </div>`;
      }).join('')}
    </div>`;
}

// Faz 10 PR4 — Ekip view delegated click bindings.
// #agent-grid → data-action=open-agent + data-agent-name
// #accounts-card → data-action=account-switch|account-remove + data-account-name
let _ekipDelegated = false;
export function bindEkipDelegation() {
  if (_ekipDelegated) return;
  const grid = document.getElementById('agent-grid');
  if (grid) {
    grid.addEventListener('click', (e) => {
      const card = asEl(e.target)?.closest<HTMLElement>('[data-action="open-agent"]');
      if (!card || !grid.contains(card)) return;
      const name = card.dataset.agentName;
      if (name) openAgentDrawer(name);
    });
  }
  const accCard = document.getElementById('accounts-card');
  if (accCard) {
    accCard.addEventListener('click', (e) => {
      const btn = asEl(e.target)?.closest<HTMLElement>('[data-action]');
      if (!btn || !accCard.contains(btn)) return;
      const name = btn.dataset.accountName;
      if (!name) return;
      if (btn.dataset.action === 'account-switch') accountSwitch(name);
      else if (btn.dataset.action === 'account-remove') accountRemove(name);
    });
  }
  _ekipDelegated = true;
}
